// AboutHero.tsx
import React from "react";

type Align = "start" | "center" | "end";

export type AboutHeroProps = {
  /** Arka plan görseli (zorunlu) */
  image: string;

  /** Küçük üst başlık (örn: "Hakkımızda") */
  kicker?: string;

  /** Büyük başlık (örn: "Biz Kimiz?") */
  title: string;

  /** Açıklama metni */
  description?: string;

  /** Renkler ve tipografi */
  overlayColor?: string;       // default: "#2cadd2"  (mavi)
  overlayOpacity?: number;     // default: 0.75
  accentColor?: string;        // default: "#e30613"  (kırmızı)
  textColor?: string;          // default: "#2b2b2b"

  /** Arka plan odak noktası (örn: "center right", "50% 40%") */
  focus?: string;              // default: "center center"

  /** Dikey hizalama: start | center | end */
  align?: Align;               // default: "center"

  /** Yükseklikler (px ya da "vh") */
  desktopHeight?: number | string; // ≥ 992px  default: 420
  tabletHeight?: number | string;  // 576–991px default: 360
  mobileHeight?: number | string;  // < 576px   default: 300

  className?: string;
};

const toCssSize = (v: number | string) => (typeof v === "number" ? `${v}px` : v);

/** #RRGGBB | #RGB -> rgba(r,g,b,a) */
function hexToRgba(hex: string, alpha = 1): string {
  const h = hex.replace("#", "");
  const parse = (s: string) => parseInt(s, 16);
  if (h.length === 3) {
    const r = parse(h[0] + h[0]);
    const g = parse(h[1] + h[1]);
    const b = parse(h[2] + h[2]);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (h.length >= 6) {
    const r = parse(h.slice(0, 2));
    const g = parse(h.slice(2, 4));
    const b = parse(h.slice(4, 6));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(44, 173, 210, ${alpha})`; // fallback
}

const AboutHero: React.FC<AboutHeroProps> = ({
  image,
  kicker = "Hakkımızda",
  title,
  description = "",

  overlayColor = "#2cadd2",
  overlayOpacity = 0.75,
  accentColor = "#e30613",
  textColor = "#2b2b2b",

  focus = "center center",
  align = "center",

  desktopHeight = 420,
  tabletHeight = 360,
  mobileHeight = 300,

  className = "",
}) => {
  const styleVars: React.CSSProperties = {
    // @ts-ignore custom properties
    "--ah-image": `url(${image})`,
    "--ah-overlay": hexToRgba(overlayColor, overlayOpacity),
    "--ah-accent": accentColor,
    "--ah-fg": textColor,
    "--ah-focus": focus,

    "--ah-height-d": toCssSize(desktopHeight),
    "--ah-height-t": toCssSize(tabletHeight),
    "--ah-height-m": toCssSize(mobileHeight),

    "--ah-align": align,
  };

  return (
    <section className={`about-hero ${className}`} style={styleVars} aria-label="Hakkımızda">
      <div className="ah-container">
        {kicker && <div className="ah-kicker">{kicker}</div>}

        <h1 className="ah-title">
          {title}
          <span className="ah-underline" />
        </h1>

        {description && (
          <p className="ah-desc">
            {description.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < description.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        )}
      </div>
    </section>
  );
};

export default AboutHero;
